import { LoaderFunction, Params } from 'react-router-dom';
import { store } from '../../../../../state/configureStore';

// GQL
import { client } from '../../../../../gql/client';
import { FETCH_ACTIONS } from '../../../../../gql/queries/actions/actions';
import { refreshOrderFlags } from '../../../../../gql/services/orderFlags';
import { FETCH_ACTIVITYS } from '../../../../../gql/queries/actions/activitys';
import { selectActionOrders } from '../../../../../gql/selectors/actions';
import {
  FETCH_CAPITOLS,
  FETCH_SUB_CAPITOLS,
  FETCH_CLASSIFICATIONS,
  FETCH_PREDEFINED_ORDERS,
} from '../../../../../gql/queries/orders/predefinedData';
import { GET_CONCESSIONS } from '../../../../../gql/queries/orders/orders';
import { GET_CONFIG } from '../../../../../gql/queries/appConfig';
import {
  SURVEILLANCE_DEFAULT_TEAM_KEY,
  parseDefaultSurveillanceTeamConfig,
} from '../../../../../hooks/useTenantDefaultSurveillanceTeam';
import { FETCH_TEAMS, FETCH_USERS } from '../../../../../gql/queries/users';
import { FETCH_EXTERNAL_COMPANIES } from '../../../../../gql/queries/actions/externalCompanies';

// Types
import { AuthContextType } from '../../../../../hooks/useAuth';
import {
  tableIds,
  takeBase,
} from '../../../../../components/display/tables/types';
import { firstPageTake } from '../../../../../utils/cursor-take.helper';

// Redux
import { getCursor } from '../../../../../state/selectors/ui/tables';
import { loadingData } from '../../../../../state/actions/ui/loadingData';
import { setCursor } from '../../../../../state/actions/ui/cursorTables';
import { FETCH_MATERIALS } from '../../../../../gql/queries/actions/materials';

// Factoría: el loader global es `makeLoader()` sin args (byte-idéntico al de
// siempre); las pestañas de un LISTADO de módulo pasan `containerTypes` +
// el `tableId` de su cursor dedicado.
//
// `getContainerId` (resolver el container desde `params`) se quedó SIN
// llamadores al retirarse las pestañas del detalle de campaña. Se conserva
// la opción, que es lo que hace reutilizable esta factoría.
//
// Fase 3 nav Trabajo/Revisión (rework, 2026-07-12) — `containerTypes` es
// ESTÁTICO (no depende de `params`, a diferencia de `getContainerId`): la
// pestaña "Actuacions" del listado del módulo RV filtra por TIPO de origen
// (`['surveillanceRoute']`), no por un `:routeId` de la URL — no hay ningún
// container concreto que resolver.
export const makeLoader = (
  options: {
    getContainerId?: (params: Params) => string | undefined;
    tableId?: string;
    containerTypes?: string[];
    // Feature surveillance_default_team (2026-07-17): la pestaña Actuacions
    // del módulo RV arranca con el filtro Equipo PRELLENADO con el equipo de
    // vigilancia por defecto del tenant ("filtrar por el equipo que venga
    // del recorrido por defecto"). Solo la PRIMERA vez por sesión: si el
    // usuario lo quita (onClearValue → teamIds undefined), el marcador de
    // sessionStorage evita re-aplicarlo en el siguiente run del loader.
    defaultTeamFromTenantConfig?: boolean;
  } = {}
): ((userData: AuthContextType | null) => LoaderFunction) => {
  const {
    getContainerId,
    tableId = tableIds.GENERAL_ACTIONS,
    containerTypes,
    defaultTeamFromTenantConfig,
  } = options;
  return (userData) =>
    async ({ params }) => {
      // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
      // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
      // el router termine la navegación al instante y el `<Fallback>` enseñe el
      // Loader DENTRO de la pantalla nueva.
      const load = async () => {
        // Banderitas (spec 2026-10-22 § 7.2): en paralelo y sin esperar; nunca lanza.
        void refreshOrderFlags(userData);
        try {
          let cursorActions = getCursor(tableId)(store.getState());
          const dispatch = store.dispatch;

          // Fase 2 nav Trabajo/Revisión — containerId contextual (id de la Order de
          // la campaña), resincronizado en CADA run del loader.
          const containerId = getContainerId?.(params);
          if (containerId && cursorActions.containerId !== containerId) {
            dispatch(setCursor(tableId, { ...cursorActions, containerId }));
          }

          // Fase 3 nav Trabajo/Revisión (rework) — containerTypes estático,
          // resincronizado en CADA run del loader (mismo criterio que containerId).
          if (
            containerTypes?.length &&
            JSON.stringify(cursorActions.containerTypes) !==
              JSON.stringify(containerTypes)
          ) {
            dispatch(setCursor(tableId, { ...cursorActions, containerTypes }));
          }

          // Fase 4 nav Trabajo/Revisión — `containerTypes` estático (RV) gana sobre
          // el cursor; en la ruta GLOBAL (`options.containerTypes` vacío) el valor
          // sale del filtro "Origen" user-driven ya persistido en `cursorActions`
          // (leído ANTES de los dispatches de arriba — igual que `containerId` unas
          // líneas más abajo, que también usa la variable local resuelta en vez de
          // releer `cursorActions` tras el dispatch).
          const effectiveContainerTypes = containerTypes?.length
            ? containerTypes
            : cursorActions.containerTypes;

          // Feature surveillance_default_team: prellenar el filtro Equipo (una vez
          // por sesión) con el equipo de vigilancia por defecto del tenant.
          const teamDefaultMarker = `rv-team-filter-default-applied:${tableId}`;
          if (
            defaultTeamFromTenantConfig &&
            !cursorActions.teamIds?.length &&
            !sessionStorage.getItem(teamDefaultMarker)
          ) {
            try {
              const { data: configData } = await client.query({
                query: GET_CONFIG,
                variables: { key: SURVEILLANCE_DEFAULT_TEAM_KEY },
                fetchPolicy: 'cache-first',
                errorPolicy: 'ignore',
              });
              const defaultTeamId = parseDefaultSurveillanceTeamConfig(
                configData?.getConfig
              );
              if (defaultTeamId) {
                cursorActions = { ...cursorActions, teamIds: [defaultTeamId] };
                dispatch(setCursor(tableId, { teamIds: [defaultTeamId] }));
              }
            } catch {
              /* sin config / offline → sin prefill */
            }
            sessionStorage.setItem(teamDefaultMarker, '1');
          }

          // Fix A4: Pre-set concessionId when there's only 1 concession to avoid Apollo cache partition splits
          let effectiveConcessionId = cursorActions.concessionId;
          if (!effectiveConcessionId) {
            try {
              const {
                data: { concessions },
              } = await client.query({ query: GET_CONCESSIONS });
              if (concessions?.length === 1) {
                effectiveConcessionId = concessions[0].id;
                dispatch(
                  setCursor(tableId, {
                    ...cursorActions,
                    concessionId: effectiveConcessionId,
                  })
                );
              }
            } catch {
              /* continue without concessionId */
            }
          }

          if (userData?.user) {
            const roleActive = userData?.user?.user?.rolActive;

            // FIX (loader-loading-flag-leak): cierra cualquier flag fugado por un run
            // anterior interrumpido. Sin esto, un HIT path con flag colgado de un run
            // previo deja la tabla bloqueada permanentemente.
            dispatch(loadingData(tableId, false));

            const readActions = client.readQuery({
              query: FETCH_ACTIONS,
              variables: {
                input: {
                  take: cursorActions.take,
                  cursor: cursorActions.cursor,
                  concessionId: effectiveConcessionId,
                  capitolId: cursorActions.capitolId,
                  subCapitolId: cursorActions.subCapitolId,
                  sortField: cursorActions.sortField,
                  orderBy: cursorActions.orderBy,
                  toDate: cursorActions.toDate,
                  fromDate: cursorActions.fromDate,
                  indexType: cursorActions.indexType,
                  indexExpedition: cursorActions.indexExpedition,
                  classification: cursorActions.classification,
                  predefinedOrderId: cursorActions.predefinedOrderId,
                  states: cursorActions.states,
                  userIds: cursorActions.userIds,
                  operatorIds: cursorActions.operatorIds,
                  teamIds: cursorActions.teamIds,
                  externalCompanyId: cursorActions.externalCompanyId,
                  pkInitKm: cursorActions?.pkInitKm,
                  pkInitMeter: cursorActions?.pkInitMeter,
                  pkEndKm: cursorActions?.pkEndKm,
                  pkEndMeter: cursorActions?.pkEndMeter,
                  description: cursorActions?.description,
                  laneCutting: cursorActions?.laneCutting,
                  materialIds: cursorActions?.materialIds,
                  hasMaterials: cursorActions?.hasMaterials,
                  track: cursorActions?.track,
                  activityId: cursorActions?.activityId,
                  // «Solo marcadas» (spec 2026-10-22 § 8.3): true o ausente.
                  onlyFlagged: cursorActions?.onlyFlagged,
                  // GOTCHA (review T1): omitir el campo en vez de null/undefined
                  // explícito — un `containerId: null` SÍ entra en la keyArgs de
                  // Apollo y partiría la caché histórica sin containerId. Misma
                  // regla para `containerTypes` (Fase 3 nav rework).
                  ...(containerId && { containerId }),
                  ...(effectiveContainerTypes?.length && {
                    containerTypes: effectiveContainerTypes,
                  }),
                },
              },
            });

            const totalCount = readActions?.actions?.totalCount;
            const pageInfo = readActions?.actions?.pageInfo;

            const isCacheEmpty = JSON.stringify(readActions) === '{}';

            const actions =
              readActions &&
              !isCacheEmpty &&
              (await selectActionOrders(readActions?.actions?.actions));

            const [
              {
                data: { capitols },
              },
              {
                data: { concessions },
              },
              {
                data: { subCapitols },
              },
              {
                data: { classifications },
              },
              {
                data: { teams },
              },
              {
                data: { externalCompanies },
              },
              {
                data: { activitys },
              },
              {
                data: { predefinedOrders },
              },
              {
                data: { users },
              },
            ] = await Promise.all([
              client.query({
                query: FETCH_CAPITOLS,
              }),
              client.query({
                query: GET_CONCESSIONS,
              }),
              client.query({
                query: FETCH_SUB_CAPITOLS,
              }),
              client.query({
                query: FETCH_CLASSIFICATIONS,
              }),
              client.query({
                query: FETCH_TEAMS,
              }),
              client.query({
                query: FETCH_EXTERNAL_COMPANIES,
              }),
              client.query({
                query: FETCH_ACTIVITYS,
                fetchPolicy: 'cache-first',
              }),
              client.query({
                query: FETCH_PREDEFINED_ORDERS,
                fetchPolicy: 'cache-first',
              }),
              client.query({
                query: FETCH_USERS,
                fetchPolicy: 'cache-first',
              }),
            ]);

            const filtersDataOptions = {
              // Opciones del filtro de materiales. Sale del catálogo ya cacheado

              // (stale-while-revalidate, `cache-only` en la 1ª lectura), así que

              // sin cobertura no tumba el loader.

              materials: await client
                .query({ query: FETCH_MATERIALS })
                .then((r: any) => r.data?.materials ?? [])
                .catch(() => []),
              capitols,
              concessions,
              subCapitols,
              classifications,
              teams,
              externalCompanies,
              activitys,
              predefinedOrders,
              users,
            };

            const combinedData = {
              actions,
              filtersDataOptions,
              totalCount,
              pageInfo,
              roleActive,
            };

            if (!readActions || isCacheEmpty) {
              dispatch(loadingData(tableId, true));
              try {
                let data;
                if (isCacheEmpty) {
                  // Tarea #11: respetar el take guardado en cursor (page-size selector).
                  const effectiveTake = firstPageTake(
                    cursorActions.take,
                    takeBase
                  );
                  data = await client.query({
                    query: FETCH_ACTIONS,
                    variables: {
                      input: {
                        take: effectiveTake,
                        cursor: '',
                        concessionId: effectiveConcessionId,
                        capitolId: cursorActions.capitolId,
                        subCapitolId: cursorActions.subCapitolId,
                        sortField: cursorActions.sortField,
                        orderBy: cursorActions.orderBy ?? 'desc',
                        toDate: cursorActions.toDate,
                        fromDate: cursorActions.fromDate,
                        indexType: cursorActions.indexType,
                        indexExpedition: cursorActions.indexExpedition,
                        classification: cursorActions.classification,
                        predefinedOrderId: cursorActions.predefinedOrderId,
                        states: cursorActions.states,
                        userIds: cursorActions.userIds,
                        operatorIds: cursorActions.operatorIds,
                        teamIds: cursorActions.teamIds,
                        externalCompanyId: cursorActions.externalCompanyId,
                        pkInitKm: cursorActions?.pkInitKm,
                        pkInitMeter: cursorActions?.pkInitMeter,
                        pkEndKm: cursorActions?.pkEndKm,
                        pkEndMeter: cursorActions?.pkEndMeter,
                        description: cursorActions?.description,
                        laneCutting: cursorActions?.laneCutting,
                        materialIds: cursorActions?.materialIds,
                        hasMaterials: cursorActions?.hasMaterials,
                        track: cursorActions?.track,
                        activityId: cursorActions?.activityId,
                        onlyFlagged: cursorActions?.onlyFlagged,
                        ...(containerId && { containerId }),
                        ...(effectiveContainerTypes?.length && {
                          containerTypes: effectiveContainerTypes,
                        }),
                      },
                    },
                  });

                  const setCursorData = {
                    take: effectiveTake,
                    cursor: '',
                    concessionId: effectiveConcessionId,
                    capitolId: cursorActions.capitolId,
                    subCapitolId: cursorActions.subCapitolId,
                    // #74: sortField/orderBy se OMITEN (el merge del reducer los preserva).
                    toDate: cursorActions.toDate,
                    fromDate: cursorActions.fromDate,
                    indexType: cursorActions.indexType,
                    indexExpedition: cursorActions.indexExpedition,
                    classification: cursorActions.classification,
                    predefinedOrderId: cursorActions.predefinedOrderId,
                    states: cursorActions.states,
                    userIds: cursorActions.userIds,
                    operatorIds: cursorActions.operatorIds,
                    teamIds: cursorActions.teamIds,
                    externalCompanyId: cursorActions.externalCompanyId,
                    pkInitKm: cursorActions?.pkInitKm,
                    pkInitMeter: cursorActions?.pkInitMeter,
                    pkEndKm: cursorActions?.pkEndKm,
                    pkEndMeter: cursorActions?.pkEndMeter,
                    description: cursorActions?.description,
                    laneCutting: cursorActions?.laneCutting,
                    materialIds: cursorActions?.materialIds,
                    hasMaterials: cursorActions?.hasMaterials,
                    track: cursorActions?.track,
                    activityId: cursorActions?.activityId,
                    onlyFlagged: cursorActions?.onlyFlagged,
                    // Redux (no Apollo): full-replace de la slice del cursor — hace
                    // falta el valor explícito para no perder el containerId/
                    // containerTypes sincronizados al principio del loader.
                    containerId,
                    containerTypes: effectiveContainerTypes,
                  };

                  dispatch(setCursor(tableId, setCursorData));
                } else {
                  data = await client.query({
                    query: FETCH_ACTIONS,
                    variables: {
                      input: {
                        take: cursorActions.take,
                        cursor: cursorActions.cursor,
                        concessionId: effectiveConcessionId,
                        capitolId: cursorActions.capitolId,
                        subCapitolId: cursorActions.subCapitolId,
                        sortField: cursorActions.sortField,
                        orderBy: cursorActions.orderBy,
                        toDate: cursorActions.toDate,
                        fromDate: cursorActions.fromDate,
                        indexType: cursorActions.indexType,
                        indexExpedition: cursorActions.indexExpedition,
                        classification: cursorActions.classification,
                        predefinedOrderId: cursorActions.predefinedOrderId,
                        states: cursorActions.states,
                        userIds: cursorActions.userIds,
                        operatorIds: cursorActions.operatorIds,
                        teamIds: cursorActions.teamIds,
                        externalCompanyId: cursorActions.externalCompanyId,
                        pkInitKm: cursorActions?.pkInitKm,
                        pkInitMeter: cursorActions?.pkInitMeter,
                        pkEndKm: cursorActions?.pkEndKm,
                        pkEndMeter: cursorActions?.pkEndMeter,
                        description: cursorActions?.description,
                        laneCutting: cursorActions?.laneCutting,
                        materialIds: cursorActions?.materialIds,
                        hasMaterials: cursorActions?.hasMaterials,
                        track: cursorActions?.track,
                        activityId: cursorActions?.activityId,
                        onlyFlagged: cursorActions?.onlyFlagged,
                        ...(containerId && { containerId }),
                        ...(effectiveContainerTypes?.length && {
                          containerTypes: effectiveContainerTypes,
                        }),
                      },
                    },
                  });
                }

                const loading = data.loading;
                const actions = await selectActionOrders(
                  data.data.actions.actions
                );

                const totalCount = data.data.actions.totalCount;

                const pageInfo = data.data.actions.pageInfo;

                const [
                  {
                    data: { capitols },
                  },
                  {
                    data: { concessions },
                  },
                  {
                    data: { subCapitols },
                  },
                  {
                    data: { classifications },
                  },
                  {
                    data: { teams },
                  },
                  {
                    data: { externalCompanies },
                  },
                  {
                    data: { activitys: activitysSecond },
                  },
                  {
                    data: { predefinedOrders },
                  },
                  {
                    data: { users },
                  },
                ] = await Promise.all([
                  client.query({
                    query: FETCH_CAPITOLS,
                  }),
                  client.query({
                    query: GET_CONCESSIONS,
                  }),
                  client.query({
                    query: FETCH_SUB_CAPITOLS,
                  }),
                  client.query({
                    query: FETCH_CLASSIFICATIONS,
                  }),
                  client.query({
                    query: FETCH_TEAMS,
                  }),
                  client.query({
                    query: FETCH_EXTERNAL_COMPANIES,
                  }),
                  client.query({
                    query: FETCH_ACTIVITYS,
                    fetchPolicy: 'cache-first',
                  }),
                  client.query({
                    query: FETCH_PREDEFINED_ORDERS,
                    fetchPolicy: 'cache-first',
                  }),
                  client.query({
                    query: FETCH_USERS,
                    fetchPolicy: 'cache-first',
                  }),
                ]);

                const filtersDataOptions = {
                  // Opciones del filtro de materiales. Sale del catálogo ya cacheado

                  // (stale-while-revalidate, `cache-only` en la 1ª lectura), así que

                  // sin cobertura no tumba el loader.

                  materials: await client
                    .query({ query: FETCH_MATERIALS })
                    .then((r: any) => r.data?.materials ?? [])
                    .catch(() => []),
                  capitols,
                  concessions,
                  subCapitols,
                  classifications,
                  teams,
                  externalCompanies,
                  activitys: activitysSecond,
                  predefinedOrders,
                  users,
                };

                const combinedData = {
                  actions,
                  filtersDataOptions,
                  totalCount,
                  pageInfo,
                  loading,
                  roleActive,
                };

                return combinedData;
              } finally {
                // FIX (loader-loading-flag-leak): cierra el flag aunque el bloque
                // MISS lance una excepción a mitad.
                dispatch(loadingData(tableId, false));
              }
            }
            return combinedData;
          }

          return undefined;
        } catch (err) {
          console.log('Loader offline fallback:', err);
          // FIX (loader-loading-flag-leak): defensa en profundidad para errores
          // antes del MISS branch (bootstrap, readQuery, selector).
          store.dispatch(loadingData(tableId, false));
          return {
            actions: [],
            filtersDataOptions: {
              capitols: [],
              concessions: [],
              subCapitols: [],
              classifications: [],
              teams: [],
              externalCompanies: [],
              activitys: [],
              predefinedOrders: [],
              users: [],
            },
            totalCount: 0,
            pageInfo: { hasNextPage: false, hasPreviousPage: false },
            roleActive: userData?.user?.user?.rolActive,
          };
        }
      };

      return { data: load() };
    };
};

// Loader global (ruta `/work-orders/actions`) — comportamiento byte-idéntico
// al anterior a la Fase 2 nav Trabajo/Revisión.
export const LoaderActionsGeneralView = makeLoader();
